import { PiuProductState } from '../companies/products/product-input-ui/types';
import { BlanketProduct } from './blanket-product/types';

export function toPiuProductState(
  blanket: BlanketProduct,
  // PLT-5109: read-only (blanketti) sets pass a pre-computed normalized revenue share here, derived
  // via the canonical blanket weight-resolution logic (`resolveProductWeightsRecursive`). When
  // omitted (the editable PIU/APG flow), the stored relative `weight` is used as-is.
  revenueShareOverride?: number,
  // PLT-5109: read-only mode, set ONLY by the read-only (old "blanketti") rendering path. It changes
  // two behaviors that must NOT leak into the editable PIU/APG flow:
  //   1. meta-less products are rendered (legacy blanketti leaves have no `meta`) instead of dropped;
  //   2. an empty `title` falls back to the backend-resolved matched product title.
  // The editable flow omits this flag and keeps the original behavior: products without `meta` are
  // dropped (returned `undefined`) and the stored `title` is used verbatim. This scoping is what
  // keeps APG/PIU sets byte-for-byte equivalent to master while still letting blanketti sets render.
  isReadOnly = false,
): PiuProductState | undefined {
  // Editable PIU/APG flow: drop products without `meta` (original behavior). Read-only blanketti
  // sets render their meta-less leaf products instead. `ipr`/`productMatchResult` stay undefined
  // when there is no meta.
  if (!blanket.meta && !isReadOnly) return undefined;
  return {
    id: blanket.id.toString(),
    // Stringify so PiuProductState ids and parentId references are the same
    // domain. Newly-added products on the client use UUIDs in `id`, so this
    // column also has to be a string.
    parentId: blanket.parentId !== null ? blanket.parentId.toString() : null,
    // Read-only blanketti leaves frequently have an empty `title` but carry a `taxonomyMapping`
    // UPG code that the backend resolves into `mappedProductInfo`; fall back to that resolved title
    // so the row renders with a readable label. Editable sets always set their own title.
    title: isReadOnly ? blanket.title || blanket.mappedProductInfo?.title || '' : blanket.title,
    description: blanket.description || '',
    revenueShare:
      revenueShareOverride !== undefined ? revenueShareOverride : blanket.weight !== null ? blanket.weight : undefined,
    productMatchResult: undefined,
    ...blanket.meta,
  };
}

/**
 * An editable set's saved products as the editor holds them, so the whole tree round-trips through
 * a save (PLT-5690). The product editor, the chat's edits and the chat's file save all read a set
 * through this, so they agree on which rows are segments (PLT-5899).
 */
export function toEditablePiuProductStates(blanketProducts: BlanketProduct[]): PiuProductState[] {
  const parentIds = new Set(
    blanketProducts.map(product => product.parentId).filter((parentId): parentId is number => parentId !== null),
  );
  return blanketProducts.map(product => toEditableRow(product, parentIds));
}

/**
 * One editable row, deciding whether the stored row is a segment. Saving writes that decision back
 * as a permanent flag that publishing obeys (PLT-5899), so a wrong "segment" drops a product from the
 * published set for good, while a wrong "product" is re-matched at publish and recoverable. The rules
 * lean towards "product" accordingly:
 *
 * 1. A row with a taxonomy mapping is a product, whatever else it carries. Company-mapping sets hold
 *    mapped rows with no `meta`, and Blanketti can map a row the editor had flagged as a segment.
 *    Such a row is still saved without its mapping (the save derives a mapping from the match, and
 *    these rows have none), so publishing re-matches it; what this rule prevents is the save
 *    flagging it as a segment, which would drop it from the published set for good.
 * 2. An unmapped row flagged `meta.isSegment` is a segment.
 * 3. An unmapped, unflagged row that still has children is a segment: how APG+ and older editor
 *    saves store one, with `meta: null` or with an auto-match blob a rename-triggered rematch put on
 *    it. Its deliberate attribute edits are kept and the auto-match artifacts dropped.
 * 4. Anything else is a product. That includes an unflagged segment that has already lost its last
 *    child, which in the data is indistinguishable from an unmatched product: older editor saves
 *    wrote company-mapping product rows in exactly that shape (`meta: null`, no mapping).
 */
function toEditableRow(product: BlanketProduct, parentIds: ReadonlySet<number>): PiuProductState {
  const stored = toPiuProductState(product) ?? rowWithoutMeta(product);
  if (product.taxonomyMapping !== null) {
    return { ...stored, isSegment: undefined };
  }
  if (stored.isSegment === true || !parentIds.has(product.id)) {
    return stored;
  }
  return { ...rowWithoutMeta(product), isSegment: true, manualIprEdits: product.meta?.manualIprEdits };
}

function rowWithoutMeta(product: BlanketProduct): PiuProductState {
  return {
    id: product.id.toString(),
    parentId: product.parentId !== null ? product.parentId.toString() : null,
    title: product.title || product.mappedProductInfo?.title || '',
    description: product.description || '',
    revenueShare: product.weight !== null ? product.weight : undefined,
    productMatchResult: undefined,
  };
}
